import { formatDate, formatMoney, pl, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { useState } from "react";

import { fill } from "./partner-cards.js";

/**
 * `round T-2`, `M5` (`P2-PAY-025`, WF-CON-18 steps 5 and 6; `DECISION ADDED 06-24`): **the
 * console's two-leg board**, one line per settlement as `GET /api/v1/payments/settlements`
 * answers it under row 26 `voirReglementsVentes`: each leg's state, how long a one-leg state has
 * lasted (the view's own days), the reason the bon is blocked in the view's own wording , the
 * same sentence the dealer reads (I30). « Confirmer sur preuve » observes Hakili's leg (row 24,
 * the administration alone): the proof filed first through the documents' ticket under the
 * settlement (`settlementEvidence`), then the door naming it. A one-leg settlement is a named
 * waiting state, never an error.
 */
export interface SettlementLineView {
  readonly id: string;
  readonly reference: string;
  readonly dossierId: string | null;
  readonly dossierReference: string | null;
  readonly vehicleLabel: string | null;
  readonly dealerName: string | null;
  readonly clientName: string | null;
  readonly salePriceAmount: number;
  readonly hakiliAmount: number;
  readonly dealerAmount: number;
  readonly status: "attenteVirements" | "voletHakiliConfirme" | "voletConcessionnaireConfirme" | "complet";
  /** The view's own word for the awaited leg(s), the api's wire value: `hakili`, `concessionnaire`, or both. */
  readonly missing: string | null;
  readonly blockedReason: string | null;
  readonly hakiliLegConfirmedAt: string | null;
  readonly dealerLegConfirmedAt: string | null;
  readonly dealerLegReceivedOn: string | null;
  readonly dealerNonReceiptReportedAt: string | null;
  readonly sinceDays: number;
  /** `T-2b`, `M2 (e)`: `banque` on the financed path, `acheteur` on a cash sale , the board names the payer. */
  readonly payerKind?: string;
  readonly voucher: {
    readonly reference: string;
    readonly issuedAt: string;
    readonly presentedAt: string | null;
  } | null;
}

export function settlementLinesFrom(answer: unknown): readonly SettlementLineView[] {
  const rows = (answer as { settlements?: unknown } | null)?.settlements;
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (one): one is SettlementLineView => typeof one === "object" && one !== null && "id" in one,
  );
}

/** The leg's own words: confirmed/received on its date, or awaited; the dealer's non-receipt named. */
export function legWords(line: SettlementLineView, leg: "hakili" | "concessionnaire"): string {
  if (leg === "hakili") {
    return line.hakiliLegConfirmedAt === null
      ? t("console.reglements.enAttente")
      : fill("console.reglements.recuLe", { date: formatDate(new Date(line.hakiliLegConfirmedAt)) });
  }
  if (line.dealerLegConfirmedAt !== null) {
    return fill("console.reglements.confirmeLe", { date: formatDate(new Date(line.dealerLegConfirmedAt)) });
  }
  if (line.dealerNonReceiptReportedAt !== null) {
    return fill("console.reglements.nonRecuDeclare", {
      date: formatDate(new Date(line.dealerNonReceiptReportedAt)),
    });
  }
  return t("console.reglements.enAttente");
}

export function Settlements({
  lines,
  failed = false,
  observe,
}: {
  readonly lines: readonly SettlementLineView[] | null;
  /** `M5`: the read was refused or failed , said as such, never drawn as an empty board. */
  readonly failed?: boolean;
  readonly observe?:
    ((settlementId: string, evidence: File, transferReference: string | null) => Promise<void>) | undefined;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState<{ id: string; word: string | null; busy: boolean } | null>(
    null,
  );
  return (
    <div
      data-testid="dossiers-reglements"
      className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      <div className="px-5 py-3">
        <h2 className="text-sm font-semibold">{t("console.reglements.titre")}</h2>
        <p className="mt-0.5 text-sm text-gray-500">{t("console.reglements.sousTitre")}</p>
      </div>
      {failed ? (
        <p data-testid="reglements-erreur" role="alert" className="px-5 pb-4 text-sm text-rose-600">
          {t("console.reglements.erreur")}
        </p>
      ) : lines === null ? (
        <p className="px-5 pb-4 text-sm text-gray-500">{t("common.state.chargement")}</p>
      ) : lines.length === 0 ? (
        <p data-testid="reglements-vide" className="px-5 pb-4 text-sm text-gray-500">
          {t("console.reglements.vide")}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {lines.map((line) => {
            const complete = line.status === "complet";
            return (
              <li key={line.id} data-testid={`reglement-${line.id}`} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="w-56 shrink-0">
                    <span className="block text-sm font-medium text-gray-900">
                      {line.dealerName ?? line.reference}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {[line.reference, line.dossierReference, line.vehicleLabel, line.clientName]
                        .filter((one) => one !== null)
                        .join(" · ")}
                    </span>
                    {/* `T-2b`: the comptant settlement beside the financed ones, its payer named (WF-X-02 step 9). */}
                    {line.payerKind === "acheteur" ? (
                      <span className="block text-xs text-indigo-700" data-testid="reglement-payeur">
                        {t("console.reglements.payeurAcheteur")}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex-1 text-xs text-gray-600" data-testid="reglement-volets">
                    <span className="block">
                      {t("console.reglements.voletConcessionnaire")} · {formatMoney(line.dealerAmount)} ·{" "}
                      {legWords(line, "concessionnaire")}
                    </span>
                    <span className="block">
                      {t("console.reglements.voletHakili")} · {formatMoney(line.hakiliAmount)} ·{" "}
                      {legWords(line, "hakili")}
                    </span>
                  </span>
                  <span className="flex w-72 shrink-0 flex-col items-end gap-1">
                    <span
                      data-testid="reglement-statut"
                      className={
                        "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                        (complete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")
                      }
                    >
                      {complete
                        ? t("console.reglements.complet")
                        : line.status === "attenteVirements"
                          ? t("console.reglements.attente")
                          : t("console.reglements.partiel")}
                    </span>
                    {complete ? (
                      <span className="text-xs text-gray-500" data-testid="reglement-bon">
                        {line.voucher === null
                          ? t("common.valeur.tiret")
                          : line.voucher.presentedAt === null
                            ? fill("console.reglements.bon", { reference: line.voucher.reference })
                            : fill("console.reglements.bonPresente", {
                                reference: line.voucher.reference,
                                date: formatDate(new Date(line.voucher.presentedAt)),
                              })}
                      </span>
                    ) : (
                      <span className="text-right text-xs text-gray-500" data-testid="reglement-motif">
                        {line.blockedReason} ·{" "}
                        {pl(
                          line.sinceDays,
                          t("console.reglements.depuis"),
                          t("console.reglements.depuisPlusieurs"),
                        )}
                      </span>
                    )}
                    {line.hakiliLegConfirmedAt !== null ? null : observe === undefined ? (
                      <GuardedButton
                        disabled
                        reason={t("console.reglements.confirmerPreuveMotif")}
                        variant="secondary"
                        testId="reglement-confirmer"
                      >
                        {t("console.reglements.confirmerPreuve")}
                      </GuardedButton>
                    ) : (
                      <button
                        type="button"
                        data-testid="reglement-confirmer"
                        onClick={() => {
                          setConfirming({ id: line.id, word: null, busy: false });
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {t("console.reglements.confirmerPreuve")}
                      </button>
                    )}
                  </span>
                </div>
                {confirming !== null && confirming.id === line.id && observe !== undefined ? (
                  <form
                    data-testid="reglement-preuve"
                    className="mt-3 rounded-xl bg-gray-50 px-4 py-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const input = event.currentTarget.elements.namedItem("evidence");
                      const reference = event.currentTarget.elements.namedItem("transferReference");
                      const file = input instanceof HTMLInputElement ? (input.files?.[0] ?? null) : null;
                      const words = reference instanceof HTMLInputElement ? reference.value.trim() : "";
                      if (file === null) {
                        setConfirming({ ...confirming, word: t("console.reglements.preuveRequise") });
                        return;
                      }
                      setConfirming({ ...confirming, busy: true, word: null });
                      void observe(confirming.id, file, words === "" ? null : words)
                        .then(() => {
                          setConfirming(null);
                        })
                        .catch((failure: unknown) => {
                          setConfirming({
                            ...confirming,
                            busy: false,
                            word:
                              (failure as { message?: string } | null)?.message ??
                              t("common.error.connexionEchouee"),
                          });
                        });
                    }}
                  >
                    <label className="block text-xs text-gray-600">
                      {t("console.reglements.confirmerPreuveMots")}
                      <input
                        name="evidence"
                        type="file"
                        accept="image/*,application/pdf"
                        data-testid="reglement-preuve-fichier"
                        className="mt-1 block text-xs"
                      />
                    </label>
                    <label className="mt-2 block text-xs text-gray-600">
                      {t("console.reglements.referenceVirement")}
                      <input
                        name="transferReference"
                        type="text"
                        data-testid="reglement-preuve-reference"
                        className="mt-1 block w-72 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                      />
                    </label>
                    {confirming.word === null ? null : (
                      <p role="alert" data-testid="reglement-refus" className="mt-1 text-xs text-rose-600">
                        {confirming.word}
                      </p>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="submit"
                        disabled={confirming.busy}
                        data-testid="reglement-valider"
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                      >
                        {t("console.reglements.confirmerPreuve")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(null);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                      >
                        {t("console.reglements.annuler")}
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
